import { usePrivy } from '@privy-io/react-auth';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { getPrivyPrimaryAddress } from '@/hooks';
import { privateV2Api } from '@/lib/api-clients/rest-client';
import { useAccountStore } from '@/store/use-account-store';

export type Trader = {
  trader: string;
  date_joined: string;
  volume_traded: number;
  fees_paid: number;
  rewards_earned: number;
};

type Referrals = {
  ref_code: string;
  traders_referred: number;
  volume_traded: number;
  rewards_earned: number | null;
  has_twitter_access: boolean;
  traders: Trader[];
  proof: {
    index: number;
    account: string;
    amount: string;
    proof: string[];
  };
  rebate_amount: string;
};

async function getReferrals(apiKey: string): Promise<Referrals> {
  return privateV2Api.get('/v2/referrals', {
    headers: {
      'X-API-KEY': apiKey,
    },
  });
}

const useReferrals = () => {
  const { user } = usePrivy();
  const address = getPrivyPrimaryAddress(user);
  const { hasAuth, apiKey } = useAccountStore(
    useShallow((state) => ({
      hasAuth: state.hasAuth,
      apiKey: state.getAuthParams(address, { skipFallback: true })?.apiKey,
    })),
  );

  return useQuery({
    enabled: hasAuth && !!address && !!apiKey,
    queryKey: ['referrals', address, apiKey],
    queryFn: () => getReferrals(apiKey!),
  });
};

export default useReferrals;
